import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Input, Select, Button, Table, Alert, Row, Col, Statistic, Tag } from 'antd';
import { motion } from 'framer-motion';
import { 
  CalculatorOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

const { Option } = Select;

interface PriceZone {
  zone: string;
  name: string;
  zipCodes: string;
  price10kg: number;
  price45kg: number;
  price100kg: number;
  price300kg: number;
  price500kg: number;
  timeframe: string;
  method: string;
}

const priceZones: PriceZone[] = [
  {
    zone: 'west',
    name: '美西头程仓',
    zipCodes: '可接托盘，不箱，海外仓卡派，派送费用单询',
    price10kg: 45.5,
    price45kg: 43.5,
    price100kg: 41.5,
    price300kg: 41.5,
    price500kg: 41,
    timeframe: '6-8天到仓',
    method: 'UPS/FEDEX派送'
  },
  {
    zone: 'west-8-9',
    name: '美西(邮编8-9)',
    zipCodes: '邮编以8、9开头的地区',
    price10kg: 49.0,
    price45kg: 47.0,
    price100kg: 45.0,
    price300kg: 45.0,
    price500kg: 44.5,
    timeframe: '7-9天提取',
    method: 'UPS/FEDEX派送'
  },
  {
    zone: 'central-4-7',
    name: '美中(邮编4-7)',
    zipCodes: '邮编以4、5、6、7开头的地区',
    price10kg: 51.0,
    price45kg: 49.0,
    price100kg: 47.0,
    price300kg: 47.0,
    price500kg: 46.5,
    timeframe: '7-9天提取',
    method: 'UPS/FEDEX派送'
  },
  {
    zone: 'east-0-3',
    name: '美东(邮编0-3)',
    zipCodes: '邮编以0、1、2、3开头的地区',
    price10kg: 52.0,
    price45kg: 50.0,
    price100kg: 48.0,
    price300kg: 48.0,
    price500kg: 47.5,
    timeframe: '7-9天提取',
    method: 'UPS/FEDEX派送'
  }
];

const discountRules = [
  { type: '重货优惠', condition: '单票50KG以上', discount: '1:250减1/KG，1:300减1.5/KG' },
  { type: '泡货优惠', condition: '单票50KG以上', discount: '分泡50%，大泡单议' },
  { type: '分区优惠', condition: '邮编90/91/92开头', discount: '优惠1/KG' }
];

const PriceCalculator: React.FC = () => {
  const [form] = Form.useForm();
  const [selectedZone, setSelectedZone] = useState<string>('west-8-9');
  const [weight, setWeight] = useState<number>(10);
  const [dimensions, setDimensions] = useState<{length: number, width: number, height: number}>({
    length: 0, width: 0, height: 0
  });
  const [calculatedPrice, setCalculatedPrice] = useState<number>(0);
  const [volumeWeight, setVolumeWeight] = useState<number>(0);
  const [chargeableWeight, setChargeableWeight] = useState<number>(0);

  const calculatePrice = useCallback(() => {
    const zone = priceZones.find(z => z.zone === selectedZone);
    if (!zone) return;

    // 计算材积重量 (材积/6000)
    const volume = dimensions.length * dimensions.width * dimensions.height;
    const volWeight = volume / 6000;
    setVolumeWeight(volWeight);

    // 取实重和材积重较大者为计费重量
    const chargeWeight = Math.max(weight, volWeight);
    const finalChargeWeight = Math.max(chargeWeight, 10); // 最低10kg计费
    setChargeableWeight(finalChargeWeight);

    // 根据重量选择对应价格
    let unitPrice = zone.price10kg;
    if (finalChargeWeight >= 500) unitPrice = zone.price500kg;
    else if (finalChargeWeight >= 300) unitPrice = zone.price300kg;
    else if (finalChargeWeight >= 100) unitPrice = zone.price100kg;
    else if (finalChargeWeight >= 45) unitPrice = zone.price45kg;

    const totalPrice = finalChargeWeight * unitPrice;
    setCalculatedPrice(totalPrice);
  }, [selectedZone, weight, dimensions]);

  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  const columns = [
    {
      title: '国家分区',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      render: (text: string, record: PriceZone) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1D1D1F' }}>{text}</div>
          <Tag color="blue" style={{ marginTop: '4px', fontSize: '12px' }}>
            {record.timeframe}
          </Tag>
        </div>
      )
    },
    { title: '10KG+', dataIndex: 'price10kg', key: 'price10kg', render: (price: number) => `¥${price}` },
    { title: '45KG+', dataIndex: 'price45kg', key: 'price45kg', render: (price: number) => `¥${price}` },
    { title: '100KG+', dataIndex: 'price100kg', key: 'price100kg', render: (price: number) => `¥${price}` },
    { title: '300KG+', dataIndex: 'price300kg', key: 'price300kg', render: (price: number) => `¥${price}` },
    { title: '500KG+', dataIndex: 'price500kg', key: 'price500kg', render: (price: number) => `¥${price}` },
    {
      title: '备注',
      dataIndex: 'method',
      key: 'method',
      render: (text: string) => (
        <Tag color="green" style={{ fontSize: '12px' }}>{text}</Tag>
      )
    }
  ];

  return (
    <section id="pricing" style={{ padding: '80px 0', background: '#f8f9fa' }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 className="display-medium" style={{ 
            marginBottom: '24px',
            color: '#1d1d1f',
          }}>
            运费计算器
          </h2>
          <p className="body-large" style={{ 
            color: '#86868B', 
            maxWidth: '600px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            快速计算您的货物运费，透明价格，无隐藏费用
          </p>
        </motion.div>

        <Row gutter={[32, 32]}>
          {/* 价格计算器 */}
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalculatorOutlined style={{ color: '#0071e3' }} />
                    <span>运费计算</span>
                  </div>
                }
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Form form={form} layout="vertical">
                  <Form.Item label="目的地区域" name="zone">
                    <Select 
                      value={selectedZone} 
                      onChange={setSelectedZone}
                      size="large"
                    >
                      {priceZones.map(zone => (
                        <Option key={zone.zone} value={zone.zone}>
                          {zone.name} - {zone.zipCodes}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item label="实际重量 (kg)" name="weight">
                    <Input
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(Number(e.target.value))}
                      placeholder="请输入实际重量"
                      size="large"
                      min={0.5}
                      step={0.1}
                    />
                  </Form.Item>

                  <Form.Item label="包装尺寸 (cm)">
                    <Row gutter={8}>
                      <Col span={8}>
                        <Input
                          placeholder="长"
                          type="number"
                          value={dimensions.length}
                          onChange={(e) => setDimensions({...dimensions, length: Number(e.target.value)})}
                          size="large"
                        />
                      </Col>
                      <Col span={8}>
                        <Input
                          placeholder="宽"
                          type="number"
                          value={dimensions.width}
                          onChange={(e) => setDimensions({...dimensions, width: Number(e.target.value)})}
                          size="large"
                        />
                      </Col>
                      <Col span={8}>
                        <Input
                          placeholder="高"
                          type="number"
                          value={dimensions.height}
                          onChange={(e) => setDimensions({...dimensions, height: Number(e.target.value)})}
                          size="large"
                        />
                      </Col>
                    </Row>
                  </Form.Item>

                  <Alert
                    message="计费说明"
                    description="计费重量 = 材积/6000，整票总材积和总实重取大者为计费重，计费重进位取整。"
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginBottom: '24px' }}
                  />
                </Form>
              </Card>
            </motion.div>
          </Col>

          {/* 计算结果 */}
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DollarOutlined style={{ color: '#52c41a' }} />
                    <span>运费预估</span>
                  </div>
                }
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  height: '100%'
                }}
              >
                <Row gutter={[16, 24]}>
                  <Col span={12}>
                    <Statistic
                      title="实际重量"
                      value={weight}
                      suffix="kg"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="材积重量"
                      value={volumeWeight}
                      precision={2}
                      suffix="kg"
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="计费重量"
                      value={chargeableWeight}
                      precision={1}
                      suffix="kg"
                      valueStyle={{ color: '#fa541c' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="预估运费"
                      value={calculatedPrice}
                      precision={2}
                      prefix="¥"
                      valueStyle={{ color: '#52c41a', fontSize: '1.5rem', fontWeight: '700' }}
                    />
                  </Col>
                </Row>

                <div style={{ 
                  marginTop: '24px', 
                  padding: '16px', 
                  background: '#f0f9ff', 
                  borderRadius: '8px',
                  border: '1px solid #91d5ff'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <ThunderboltOutlined style={{ color: '#1890ff' }} />
                    <span style={{ fontWeight: '600', color: '#1890ff' }}>时效保障</span>
                  </div>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    {priceZones.find(z => z.zone === selectedZone)?.timeframe}，全程透明，无隐箱操作
                  </p>
                </div>

                <Button
                  type="primary"
                  size="large"
                  style={{
                    width: '100%',
                    marginTop: '24px',
                    background: '#0071e3',
                    border: 'none',
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  立即下单
                </Button>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {/* 价格表 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ marginTop: '60px' }}
        >
          <Card
            title="专线报价表"
            style={{
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Table
              columns={columns}
              dataSource={priceZones}
              rowKey="zone"
              pagination={false}
              scroll={{ x: 800 }}
            />
            
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ marginBottom: '16px', color: '#1D1D1F' }}>优惠政策</h4>
              <Row gutter={[16, 16]}>
                {discountRules.map((rule, index) => (
                  <Col xs={24} md={8} key={index}>
                    <div style={{
                      padding: '16px',
                      background: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: '8px'
                    }}>
                      <h5 style={{ color: '#52c41a', margin: '0 0 8px 0' }}>{rule.type}</h5>
                      <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
                        <strong>条件：</strong>{rule.condition}
                      </p>
                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                        <strong>优惠：</strong>{rule.discount}
                      </p>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default PriceCalculator;

